// src/components/ProfileDialog.jsx
// "My profile" window: edit your name, phone and WhatsApp numbers, see your role and
// group, your time zone and secondary email. Password and two-factor
// authentication moved to Settings (3.9.1): PasswordSection and
// TwoFactorSection are exported for SettingsDialog.jsx.
import { phone as checkPhone, display as showPhone, PHONE_HELP } from '../utils/phone';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { updateMe, whatsappSendCode, whatsappConfirm, fetchMe, fetchMfaStatus, createRecoveryCodes, forgetRememberedDevices, addSecondaryEmail, removeSecondaryEmail } from '../utils/api';
import { rememberedDeviceId, signInMethods, connectedAccountOf } from '../hooks/useAuth';
import { passwordProblem } from '../pages/ChooseOwnPassword';
import { usePreferences } from '../hooks/usePreferences';
import { COMMON_TIME_ZONES, allTimeZones, deviceTimeZone, timeZoneName } from '../utils/timeZone';
import { groupLabelFor, roleLabelFor, isSuperAdmin } from '../utils/account';
import { ReauthBox, RecoveryCodesPanel, friendlyTwoFactorError } from './TwoFactorSetup';
import { timeZone, tzLabel } from '../utils/timeZone';
import Avatar from './Avatar';
import Loader from './Loader';
import Emo from './Emo';
import { PILL, PILL_ON } from '../theme/tabs';

// only='timeZone' (3.22.2): just the time zone, as its own small pop-up
// (from the clock above the menu); onPicture: open Profile picture
// 3.73.0 (owner, Oct 30): two tabs, Profile (details, secondary email,
// default company for faxes by email) and Time zone, as in Security
const PROFILE_TABS = [
  { id: 'profile', label: 'Profile', icon: '👤' },
  { id: 'timeZone', label: 'Time zone', icon: '🕒' },
];

export default function ProfileDialog({ onClose, focus, only = null, onPicture }) {
  const { user, userInfo, getToken, updateUserInfo, setError } = useAuth();
  const [tab, setTab] = useState(focus === 'timeZone' ? 'timeZone' : 'profile');
  const { notify } = useNotify();
  const methods  = signInMethods(user);
  const isGoogle = methods.google;
  const shownName = userInfo?.name || user?.displayName || user?.email;

  const closeRef  = useRef(null);
  const returnRef = useRef(null);

  useEffect(() => {
    setError('');
    returnRef.current = document.activeElement;
    closeRef.current?.focus();
    function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); returnRef.current?.focus?.(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <div style={S.header} className="dlg-head">
          <div style={S.who}>
            {only === 'timeZone' ? <span style={{ fontSize: 28 }} aria-hidden="true"><Emo e="🕒" /></span>
              : <button onClick={onPicture} style={S.avatarBtn} title="Change your profile picture" aria-label="Change your profile picture">
                  <Avatar avatar={userInfo?.avatar} name={shownName} size={42} />
                  <span style={S.avatarEdit} aria-hidden="true">✎</span>
                </button>}
            <div style={{ minWidth: 0 }}>
              <h2 id="profile-title" style={S.title}>{only === 'timeZone' ? <><Emo e="🕒" /> Time zone</> : <><Emo e="👤" /> My profile</>}</h2>
              <div style={S.sub}>{only === 'timeZone' ? 'The same setting as in My profile' : user?.email}</div>
            </div>
          </div>
          <button ref={closeRef} onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>

        {only !== 'timeZone' && (
          <div role="tablist" aria-label="My profile" style={S.tabs}>
            {PROFILE_TABS.map(t => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                style={{ ...S.tab, ...(tab === t.id ? S.tabOn : {}) }}>
                <Emo e={t.icon} /> {t.label}
              </button>
            ))}
          </div>
        )}
        {only === 'timeZone' || tab === 'timeZone' ? (
          <div style={S.body}><TimeZoneSection notify={notify} focus={only === 'timeZone' || focus === 'timeZone'} inTab /></div>
        ) : (
        <div style={S.body}>
          <DetailsSection user={user} userInfo={userInfo} getToken={getToken}
            updateUserInfo={updateUserInfo} notify={notify} isGoogle={isGoogle} />
          <SecondaryEmailSection getToken={getToken} notify={notify} isGoogle={isGoogle} />
          <EmailDefaultSection getToken={getToken} notify={notify} />
          <p style={S.note}>Your password and two-factor authentication are under <strong>Security</strong> (the menu under your name).</p>
        </div>
        )}
      </div>
    </div>
  );
}

// ── Time zone ────────────────────────────────────────────────
// Every time in the portal is shown in it, on any browser (saved with
// their settings). Eastern until they choose.
function TimeZoneSection({ notify, focus, inTab = false }) {
  const { prefs, setPreference } = usePreferences();
  const ref = useRef(null);
  // Opened from the clock in the header: go straight to the time zone
  useEffect(() => {
    if (!focus) return;
    const t = setTimeout(() => { ref.current?.scrollIntoView({ block: 'center' }); ref.current?.focus(); }, 60);
    return () => clearTimeout(t);
  }, [focus]);
  const current = prefs.timeZone;
  const device = deviceTimeZone();
  const common = COMMON_TIME_ZONES.map(z => z.id);
  const others = allTimeZones().filter(z => !common.includes(z));
  const nowIn = tz => new Date().toLocaleString('en-US', { timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit' });

  function choose(tz) {
    setPreference('timeZone', tz);
    notify({ tone: 'success', title: `Times now show in ${timeZoneName(tz)}`, detail: `It's ${nowIn(tz)} ${tzLabel(new Date(), tz)} there. This applies wherever you sign in.` });
  }

  return (
    <section style={inTab ? undefined : S.section}>
      <h3 style={{ ...S.sectionTitle, ...(inTab ? { marginTop: 0 } : {}) }}>Time zone</h3>
      <p style={S.status}><span>Every date and time in the portal is shown in this time zone, on any computer you sign in from.</span></p>
      <div style={S.row}>
        <select ref={ref} value={current} onChange={e => choose(e.target.value)} aria-label="Time zone" style={{ ...S.input, flex: '1 1 260px' }}>
          <optgroup label="Common">
            {COMMON_TIME_ZONES.map(z => <option key={z.id} value={z.id}>{z.label} · {nowIn(z.id)}</option>)}
          </optgroup>
          <optgroup label="All time zones">
            {others.map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
          </optgroup>
        </select>
        {device !== current && (
          <button onClick={() => choose(device)} style={S.secondaryBtn} title="The time zone this computer is set to">
            Use this device's ({timeZoneName(device)})
          </button>
        )}
      </div>
    </section>
  );
}

// ── Default account for faxes sent by email (3.49.10) ────────
// Only for people in both a business and an individual client: a fax they
// email to a number address (15550100142@crazypixels.org) asks which
// account to send it from, and goes from this one after an hour without
// an answer.
function EmailDefaultSection({ getToken, notify }) {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { fetchMe(getToken).then(setMe).catch(() => setMe({})); }, [getToken]);
  const choices = me?.emailDefaultChoices || [];
  if (choices.length < 2) return null;
  const current = me.emailDefaultAccount || choices.find(c => c.type === 'business')?.id || choices[0].id;
  async function choose(id) {
    setBusy(true);
    try {
      const saved = await updateMe({ emailDefaultAccount: id }, getToken);
      if (saved.error) throw new Error(saved.error);
      setMe(m => ({ ...m, emailDefaultAccount: id }));
      notify({ tone: 'success', title: `Default account: ${choices.find(c => c.id === id)?.name}` });
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t save it', detail: e.message }); }
    setBusy(false);
  }
  return (
    <section style={S.section}>
      <h3 style={S.sectionTitle}>Default account for faxes sent by email</h3>
      <p style={S.status}><span>You’re in more than one account. When you email a fax to a number address (like 15550100142@crazypixels.org), we ask which account to send it from. If you don’t answer within an hour, it goes from this one.</span></p>
      <div style={S.row}>
        <select value={current} disabled={busy} onChange={e => choose(e.target.value)} aria-label="Default account for faxes sent by email" style={{ ...S.input, flex: '1 1 260px' }}>
          {choices.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type === 'individual' ? 'individual' : 'business'})</option>)}
        </select>
      </div>
    </section>
  );
}

// ── Secondary email ──────────────────────────────────────────
// One more address to sign in with (same password and two-factor); faxes
// emailed from it count as theirs. Typed addresses need the emailed link.
function SecondaryEmailSection({ getToken, notify, isGoogle }) {
  const [me, setMe]       = useState(null);
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy]   = useState('');
  const [err, setErr]     = useState('');

  useEffect(() => { fetchMe(getToken).then(setMe).catch(() => setMe({})); }, [getToken]);
  const current = me?.secondaryEmail;
  const pending = me?.secondaryPending;
  const day = iso => new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ' + tzLabel(new Date(iso));

  async function send(e, address = email) {
    e?.preventDefault();
    setErr(''); setBusy('send');
    try {
      const r = await addSecondaryEmail(address.trim(), getToken);
      setMe(m => ({ ...m, secondaryPending: r.pending }));
      setEditing(false); setEmail('');
      notify({ tone: 'success', title: 'Check that inbox', detail: `We emailed a confirm link to ${r.pending.email}.` });
    } catch (e2) { setErr(e2.message); }
    setBusy('');
  }
  async function remove(pendingOnly) {
    setErr(''); setBusy(pendingOnly ? 'cancel' : 'remove');
    try {
      await removeSecondaryEmail(getToken, pendingOnly);
      setMe(m => (pendingOnly ? { ...m, secondaryPending: null } : { ...m, secondaryEmail: null, secondarySource: null, secondaryPending: null }));
      notify({ tone: 'success', title: pendingOnly ? 'Cancelled' : 'Secondary email removed' });
    } catch (e2) { setErr(e2.message); }
    setBusy('');
  }

  return (
    <section style={S.section}>
      <h3 style={S.sectionTitle}>Secondary email</h3>
      {!me ? <Loader size="sm" /> : (
        <>
          {current ? (
            <p style={S.status}>
              <span style={{ ...S.pill, ...S.pillOn }}>{me.secondarySource === 'auto' ? 'Added for you' : 'Confirmed'}</span>
              <span><strong>{current}</strong>. You can sign in with it and your password{isGoogle ? ' (for Google, keep using your main account)' : ''}, and faxes you send by email from it count as yours.</span>
            </p>
          ) : !pending && (
            <p style={S.status}><span>Add another email you use for work, to sign in with it and send faxes by email from it.</span></p>
          )}
          {pending && (
            <div style={S.box}>
              <p style={S.boxText}>We emailed a confirm link to <strong>{pending.email}</strong>. It works until {day(pending.expiresAt)}.</p>
              <div style={S.row}>
                <button onClick={() => send(null, pending.email)} disabled={!!busy} style={S.secondaryBtn}>{busy === 'send' ? 'Sending…' : 'Send again'}</button>
                <button onClick={() => remove(true)} disabled={!!busy} style={S.linkBtn}>Cancel</button>
              </div>
            </div>
          )}
          {editing ? (
            <form onSubmit={send} style={{ ...S.row, marginTop: 8 }}>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                placeholder="name@company.com" aria-label="Secondary email" style={{ ...S.input, flex: '1 1 220px' }} />
              <button type="submit" disabled={!!busy || !email.trim()} style={S.saveBtn}>{busy === 'send' ? 'Sending…' : 'Send confirm link'}</button>
              <button type="button" onClick={() => { setEditing(false); setErr(''); }} style={S.linkBtn}>Cancel</button>
            </form>
          ) : !pending && (
            <div style={S.row}>
              <button onClick={() => setEditing(true)} disabled={!!busy} style={S.secondaryBtn}>{current ? 'Change' : 'Add secondary email'}</button>
              {current && <button onClick={() => remove(false)} disabled={!!busy} style={S.linkBtn}>{busy === 'remove' ? 'Removing…' : 'Remove'}</button>}
            </div>
          )}
          {err && <div style={{ ...S.error, marginTop: 10 }} role="alert">{err}</div>}
        </>
      )}
    </section>
  );
}

// 3.85.0 (owner, Nov 5): a WhatsApp number can't receive sign-in codes until
// its owner types back a code sent to it
function WhatsappConfirm({ confirmed, getToken, updateUserInfo, notify }) {
  const [sent, setSent] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  async function send() {
    setErr(''); setBusy(true);
    try {
      const r = await whatsappSendCode(getToken);
      if (r.error) throw new Error(r.error);
      setSent(r.sentTo); setCode('');
    } catch (e) {
      // 3.85.3 (owner): the box always opens, so a code that did arrive can be typed in
      setSent(sent || 'your WhatsApp');
      setErr(e.message);
    }
    setBusy(false);
  }
  async function confirm() {
    setErr(''); setBusy(true);
    try {
      const r = await whatsappConfirm(code, getToken);
      if (r.error) throw new Error(r.error);
      updateUserInfo({ whatsappConfirmed: true });
      setSent(''); setCode('');
      notify({ tone: 'success', title: 'WhatsApp number confirmed' });
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  // Its own full-width row under the two columns (3.85.1, owner)
  if (confirmed) return <p style={S.waRow}>✓ Confirmed. This number can receive sign-in codes.</p>;
  return (
    <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
      <p style={{ ...S.note, marginTop: 0 }}>Not confirmed yet. Confirm it so it can receive sign-in codes.</p>
      {!sent ? (
        <div style={{ ...S.row, marginTop: 8 }}>
          <button type="button" onClick={send} disabled={busy} style={S.secondaryBtn}>{busy ? 'Sending…' : 'Send a code'}</button>
        </div>
      ) : (
        <div style={{ ...S.row, marginTop: 8 }}>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code"
            placeholder="123456" aria-label={`The code sent to ${sent}`} style={{ ...S.input, width: 110 }} />
          <button type="button" onClick={confirm} disabled={busy || code.length !== 6} style={S.secondaryBtn}>{busy ? 'Checking…' : 'Confirm'}</button>
          <button type="button" onClick={send} disabled={busy} style={S.linkBtn}>Send a new code</button>
        </div>
      )}
      {err && <div style={{ ...S.error, marginTop: 8 }} role="alert">{err}</div>}
    </div>
  );
}

// ── Name, phone, role, client, teams ─────────────────────────
function DetailsSection({ user, userInfo, getToken, updateUserInfo, notify, isGoogle }) {
  const [name, setName]   = useState(userInfo?.name || user?.displayName || '');
  const [phone, setPhone] = useState(showPhone(userInfo?.phone));   // 3.49.17: shown as (800) 766-8732
  // 3.57.3: optional WhatsApp number, for alerts by WhatsApp
  const [whatsapp, setWhatsapp] = useState(showPhone(userInfo?.whatsapp));
  // 3.62.25 (owner): extension (optional), printed on cover sheets after the phone
  const [ext, setExt] = useState(userInfo?.phoneExt || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');
  const typedPhone = checkPhone(phone);
  const typedWhatsapp = checkPhone(whatsapp);
  const changed = name.trim() !== (userInfo?.name || '') || (typedPhone.ok ? typedPhone.e164 : phone.trim()) !== (userInfo?.phone || '')
    || (typedWhatsapp.ok ? typedWhatsapp.e164 : whatsapp.trim()) !== (userInfo?.whatsapp || '')
    || ext.trim() !== (userInfo?.phoneExt || '');

  async function save(e) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('Please enter your name.'); return; }
    if (!typedPhone.ok) { setErr(`That isn't a valid phone number. ${PHONE_HELP}`); return; }
    if (!typedWhatsapp.ok) { setErr(`That isn't a valid WhatsApp number. ${PHONE_HELP}`); return; }
    if (ext.trim() && !/^\d{1,8}$/.test(ext.trim())) { setErr('An extension is digits only, up to 8 (for example 123).'); return; }
    setSaving(true);
    try {
      const saved = await updateMe({ name: name.trim(), phone: typedPhone.e164, phoneExt: ext.trim(), whatsapp: typedWhatsapp.e164 }, getToken);
      if (saved.error) throw new Error(saved.error);
      updateUserInfo({ name: saved.name, phone: saved.phone, phoneExt: saved.phoneExt || '', whatsapp: saved.whatsapp, whatsappConfirmed: !!saved.whatsappConfirmed });
      setName(saved.name); setPhone(showPhone(saved.phone)); setExt(saved.phoneExt || ''); setWhatsapp(showPhone(saved.whatsapp));
      notify({ tone: 'success', title: 'Profile saved' });
    } catch (e2) {
      setErr(e2.message);
    }
    setSaving(false);
  }

  return (
    <section>
      <form onSubmit={save} style={S.form}>
        <div style={S.twoCols}>
          <label style={S.label}>Name
            <input value={name} onChange={e => setName(e.target.value)} maxLength={80}
              autoComplete="name" style={S.input} />
          </label>
          {/* 3.62.26: the extension sits beside the phone number, as a small box */}
          <div style={S.phoneRow}>
            <label style={{ ...S.label, flex: 1, minWidth: 0 }}><span>Phone number <span style={S.optional}>(optional)</span></span>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                onBlur={() => { const p = checkPhone(phone); if (p.ok && p.display) setPhone(p.display); }}
                autoComplete="tel" placeholder="e.g. (800) 766-8732" style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} />
            </label>
            <label style={{ ...S.label, width: 84, flexShrink: 0 }}
              title="Optional. Shown after your phone number on your fax cover sheets."><span>Ext.</span>
              <input value={ext} onChange={e => setExt(e.target.value.replace(/\D/g, '').slice(0, 8))} inputMode="numeric"
                placeholder="123" aria-label="Phone extension (optional)" style={{ ...S.input, width: '100%', boxSizing: 'border-box' }} />
            </label>
          </div>
          <label style={S.label}><span>WhatsApp number <span style={S.optional}>(optional)</span></span>
            <input type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)}
              onBlur={() => { const p = checkPhone(whatsapp); if (p.ok && p.display) setWhatsapp(p.display); }}
              placeholder="e.g. +91 98765 43210 or (800) 766-8732" style={S.input} />
          </label>
          {userInfo?.whatsapp && (typedWhatsapp.ok ? typedWhatsapp.e164 : whatsapp.trim()) === userInfo.whatsapp && (
            <WhatsappConfirm confirmed={!!userInfo.whatsappConfirmed} getToken={getToken} updateUserInfo={updateUserInfo} notify={notify} />
          )}
          <p style={S.waRow}>Your email can always receive sign-in codes. WhatsApp is optional.</p>
        </div>
        {err && <div style={S.error} role="alert">{err}</div>}
        <button type="submit" disabled={saving || !changed}
          style={saving || !changed ? { ...S.saveBtn, ...S.btnOff } : S.saveBtn}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </form>
      <dl style={S.details}>
        <Field label="Email" value={user?.email} />
        <Field label="Role"  value={roleLabelFor(user?.email, userInfo)} />
        <Field label={isSuperAdmin(userInfo, user?.email) ? 'Client' : 'Company'} value={groupLabelFor(user?.email, userInfo)} />   {/* 3.67.5: their company */}
        {/* Their teams at the picked client (3.19.1) */}
        {(userInfo?.teams || []).length > 0 && (
          <Field label={userInfo.teams.length === 1 ? 'Team' : 'Teams'}
            value={userInfo.teams.map(t => `${t.name}${userInfo.teams.length > 1 && t.id === userInfo.defaultTeam ? ' (default)' : ''}`).join(', ')} />
        )}
        <Field label="Signs in with" value={(m => [m.password && 'email and password', m.google && 'Google', m.microsoft && 'Microsoft', m.apple && 'Apple'].filter(Boolean).join(', ').replace(/^./, c => c.toUpperCase()).replace(/, ([^,]*)$/, ' or $1'))(signInMethods(user))} />
      </dl>
      <p style={S.note}>Your role, client and teams are set by your administrator.</p>
    </section>
  );
}

// ── Password ─────────────────────────────────────────────────
// Change your password, or set one when you only sign in with Google (then
// either way works). Firebase wants a recent sign-in for this: if needed we
// ask them to confirm it's them (password or Google, plus the 2FA code).
export function PasswordSection({ notify, inTab = false }) {
  const { user, setOwnPassword, userVersion } = useAuth();
  const methods = signInMethods(user);
  const hasPassword = methods.password;
  const [next, setNext]   = useState('');
  const [again, setAgain] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');
  const [reauth, setReauth] = useState(false);

  async function save(e) {
    e?.preventDefault();
    setErr('');
    const problem = passwordProblem(next);
    if (problem) { setErr(problem); return; }
    if (next !== again) { setErr("The two passwords don't match."); return; }
    setSaving(true);
    try {
      await setOwnPassword(next);
      setNext(''); setAgain(''); setReauth(false);
      notify({ tone: 'success', title: hasPassword ? 'Password changed' : 'Password set', detail: hasPassword ? undefined : 'You can now sign in with Google or with your email and this password.' });
    } catch (e2) {
      if (e2.code === 'auth/requires-recent-login' || e2.code === 'auth/user-token-expired') setReauth(true);
      else if (e2.code === 'auth/weak-password') setErr('Please choose a stronger password.');
      else if (e2.code === 'auth/credential-already-in-use' || e2.code === 'auth/email-already-in-use') setErr('A different sign-in already uses this email. Please contact Fax Portal support.');
      else setErr(e2.message || 'Something went wrong. Please try again.');
    }
    setSaving(false);
  }

  return (
    <section style={inTab ? undefined : S.section} key={userVersion}>
      {!inTab && <h3 style={S.sectionTitle}>Password</h3>}
      <p style={S.note}>
        {hasPassword
          ? `Change the password you sign in with${methods.google ? '. You can also keep signing in with Google' : ''}.`
          : 'You sign in with Google. Set a password to also sign in with your email and password (your secondary email works too).'}
      </p>
      {reauth ? (
        <ReauthBox text="For your security, please confirm it's you, then we'll save your new password."
          onConfirmed={() => { setReauth(false); save(); }} onCancel={() => setReauth(false)} />
      ) : (
        <form onSubmit={save} style={S.form}>
          <div style={S.twoCols}>
            <label style={S.label}>New password
              <input type="password" value={next} onChange={e => setNext(e.target.value)}
                required autoComplete="new-password" placeholder="At least 8 characters, letters and numbers" style={S.input} />
            </label>
            <label style={S.label}>Type it again
              <input type="password" value={again} onChange={e => setAgain(e.target.value)}
                required autoComplete="new-password" style={S.input} />
            </label>
          </div>
          {err && <div style={S.error} role="alert">{err}</div>}
          <button type="submit" disabled={saving} style={S.saveBtn}>
            {saving ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
          </button>
        </form>
      )}
    </section>
  );
}

// ── Sign in with Microsoft (3.72.0, owner, Oct 30) and Apple (3.87.0) ──
// Connect a Microsoft or Apple account to this portal account; then
// "Continue with Microsoft" or "Continue with Apple" signs in to it (the code
// from the authenticator app is still asked). One with the same email as
// the portal account is joined on the sign-in page without this (3.72.1).
const SIGN_IN_WAYS = {
  microsoft: { id: 'microsoft.com', name: 'Microsoft',
    about: <>Use your Microsoft account (work Microsoft 365, Outlook or Hotmail) to sign in. If it uses your work email, just click <strong>Continue with Microsoft</strong> on the sign-in page. If it uses a different email, connect it once here. You'll still enter the code from your authenticator app.</> },
  apple: { id: 'apple.com', name: 'Apple',
    about: <>Use your Apple account to sign in. If it uses your work email, just click <strong>Continue with Apple</strong> on the sign-in page. If it uses a different email, or you want to use Hide My Email, connect it once here. You'll still enter the code from your authenticator app.</> },
};
export function ConnectedSignInSection({ notify, way }) {
  const { id, name, about } = SIGN_IN_WAYS[way];
  const { user, userVersion, connectSignIn, disconnectSignIn } = useAuth();
  const { confirm } = useNotify();
  const connected = signInMethods(user)[way];
  const account = connectedAccountOf(user, id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [reauth, setReauth] = useState(null);   // 'connect' | 'disconnect'

  const fail = (e, again) => {
    if (e.code === 'auth/requires-recent-login' || e.code === 'auth/user-token-expired') { setReauth(again); return; }
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request' || e.code === 'auth/user-cancelled') { setErr(`The ${name} window was closed before you finished. Nothing changed.`); return; }
    if (e.code === 'auth/popup-blocked') { setErr(`Your browser blocked the ${name} window. Allow pop-ups for fax.crazypixels.org and try again.`); return; }
    if (e.code === 'auth/credential-already-in-use' || e.code === 'auth/account-exists-with-different-credential' || e.code === 'auth/email-already-in-use') {
      setErr(`That ${name} account is already used by another Fax Portal sign-in. Pick a different ${name} account, or contact Fax Portal support.`); return;
    }
    setErr(e.message || 'Something went wrong. Please try again.');
  };

  async function connect() {
    setErr(''); setBusy(true);
    try {
      await connectSignIn(way);
      setReauth(null);
      notify({ tone: 'success', title: `${name} connected`, detail: `You can now use Continue with ${name} on the sign-in page.` });
    } catch (e) { fail(e, 'connect'); }
    setBusy(false);
  }
  async function disconnect() {
    const ok = await confirm({ title: `Disconnect ${name}?`, message: `Continue with ${name} will stop working for your account${account ? ` (${account})` : ''}. You can still sign in with your email and password${signInMethods(user).google ? ' or Google' : ''}, and connect ${name} again any time.`, confirmLabel: 'Disconnect' });
    if (!ok) return;
    setErr(''); setBusy(true);
    try {
      await disconnectSignIn(way);
      setReauth(null);
      notify({ tone: 'success', title: `${name} disconnected` });
    } catch (e) { fail(e, 'disconnect'); }
    setBusy(false);
  }

  return (
    <section style={S.section} key={userVersion}>
      <h3 style={S.sectionTitle}>Sign in with {name}</h3>
      <p style={S.note}>
        {connected
          ? <>Connected{account ? <> to <strong>{account}</strong></> : ''}. On the sign-in page, <strong>Continue with {name}</strong> signs in to your account. You'll still enter the code from your authenticator app.</>
          : about}
      </p>
      {reauth ? (
        <ReauthBox text="For your security, please confirm it's you first."
          onConfirmed={() => { const what = reauth; setReauth(null); if (what === 'connect') connect(); else disconnect(); }} onCancel={() => setReauth(null)} />
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          {err && <div style={S.error} role="alert">{err}</div>}
          {connected
            ? <button type="button" onClick={disconnect} disabled={busy} style={{ ...S.saveBtn, background: 'var(--c-ffffff)', color: 'var(--t-c62828)', border: '1px solid var(--c-ef9a9a)', ...(busy ? S.btnOff : {}) }}>{busy ? 'Working…' : `Disconnect ${name}`}</button>
            : <button type="button" onClick={connect} disabled={busy} style={{ ...S.saveBtn, ...(busy ? S.btnOff : {}) }}>{busy ? `Opening ${name}…` : `Connect ${name}`}</button>}
        </div>
      )}
    </section>
  );
}

// ── Two-factor authentication (authenticator app) ────────────
// Required for everyone, so there's no "off": people can replace their
// authenticator (the app then asks for a new one straight away) and make
// a new set of recovery codes.
export function TwoFactorSection({ notify, inTab = false }) {
  const { user, userVersion, isTotpEnabled, disableTotp, getToken } = useAuth();
  const enabled = isTotpEnabled(user);

  // step: 'idle' | 'confirm-replace' | 'reauth-replace' | 'reauth-codes' | 'codes'
  const [step, setStep]     = useState('idle');
  const [status, setStatus] = useState(null);   // { recoveryCodesLeft }
  const [codes, setCodes]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchMfaStatus(getToken).then(r => { if (!cancelled) setStatus(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [getToken, userVersion, codes]);

  function reset() { setStep('idle'); setErr(''); }

  async function replace() {
    setErr(''); setBusy(true);
    try {
      await disableTotp();   // the app now shows the setup screen
    } catch (e) {
      if (e.code === 'auth/requires-recent-login') setStep('reauth-replace');
      else if (e.code === 'auth/user-token-expired') notify({ tone: 'info', title: 'Your old authenticator was removed', detail: 'Please sign in again to set up a new one.' });
      else setErr(friendlyTwoFactorError(e));
    }
    setBusy(false);
  }

  async function newCodes() {
    setErr(''); setBusy(true);
    try {
      const r = await createRecoveryCodes(() => user.getIdToken(true));
      setCodes(r.codes);
      setStep('codes');
    } catch (e) {
      if (e.code === 'REAUTH_REQUIRED') setStep('reauth-codes');
      else setErr(e.message);
    }
    setBusy(false);
  }

  const left = status?.recoveryCodesLeft;
  return (
    <section style={inTab ? undefined : S.section}>
      {!inTab && <h3 style={S.sectionTitle}>Two-factor authentication</h3>}
      <p style={S.status}>
        <span style={{ ...S.pill, ...(enabled ? S.pillOn : S.pillOff) }}>{enabled ? 'On' : 'Not set up'}</span>
        <span>Required for everyone who uses the Fax Portal. A 6-digit code from your authenticator app is needed when you sign in, except on devices you chose to remember{status?.rememberDays ? ` (for ${status.rememberDays} days)` : ''}.</span>
      </p>
      {enabled && left != null && (
        <p style={{ ...S.status, marginTop: 0 }}>
          <span style={{ ...S.pill, ...(left > 0 ? S.pillOn : S.pillWarn) }}>{left} of 10</span>
          <span>{left > 0
            ? 'recovery codes left. Each one lets you sign in once if you lose your phone.'
            : 'recovery codes left. Make a new set now, so you can get back in if you lose your phone.'}</span>
        </p>
      )}

      {step === 'idle' && enabled && (
        <div style={S.row}>
          <button onClick={newCodes} disabled={busy} style={S.saveBtn}>{busy ? 'Please wait…' : 'Get new recovery codes'}</button>
          <button onClick={() => setStep('confirm-replace')} disabled={busy} style={S.secondaryBtn}>Replace authenticator</button>
        </div>
      )}

      {step === 'confirm-replace' && (
        <div style={S.box}>
          <p style={S.boxText}>
            Replace your authenticator, for example because you have a new phone? Your current app and your recovery codes will
            stop working for the portal, and you'll set up the new one straight away.
          </p>
          <div style={S.row}>
            <button onClick={replace} disabled={busy} style={S.dangerBtn}>{busy ? 'Removing…' : 'Replace authenticator'}</button>
            <button onClick={reset} style={S.linkBtn}>Cancel</button>
          </div>
        </div>
      )}

      {step === 'reauth-replace' && <ReauthBox onConfirmed={() => { setStep('confirm-replace'); replace(); }} onCancel={reset} />}
      {step === 'reauth-codes' && <ReauthBox onConfirmed={() => { setStep('idle'); newCodes(); }} onCancel={reset} />}

      {step === 'codes' && codes && (
        <RecoveryCodesPanel codes={codes} doneLabel="Done"
          intro="Your old recovery codes no longer work."
          onDone={() => { setCodes(null); setStep('idle'); notify({ tone: 'success', title: 'New recovery codes saved' }); }} />
      )}

      {enabled && (status?.rememberedDevices || []).length > 0 && (
        <RememberedDevices devices={status.rememberedDevices} thisId={rememberedDeviceId(user?.email)}
          onForget={async id => {
            setErr('');
            try {
              await forgetRememberedDevices(id, getToken);
              setStatus(s => ({ ...s, rememberedDevices: id ? s.rememberedDevices.filter(d => d.id !== id) : [] }));
              notify({ tone: 'success', title: id ? 'Device forgotten' : 'All devices forgotten', detail: 'The code will be asked there at the next sign-in.' });
            } catch (e) { setErr(e.message); }
          }} />
      )}

      {err && <div style={{ ...S.error, marginTop: 10 }} role="alert">{err}</div>}
    </section>
  );
}

// Devices where the code isn't asked (chosen at sign-in), with Forget
function RememberedDevices({ devices, thisId, onForget }) {
  const [busy, setBusy] = useState('');
  const day = iso => new Date(iso).toLocaleDateString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric' });
  async function forget(id) { setBusy(id || 'all'); await onForget(id); setBusy(''); }
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t-37474f)', marginBottom: 6 }}>Remembered devices</div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {devices.map(d => (
          <li key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--c-eceff1)', borderRadius: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--t-37474f)', minWidth: 0 }}>
              <strong>{d.label}</strong>{d.id === thisId ? ' · this device' : ''}
              <span style={{ display: 'block', fontSize: 12, color: 'var(--t-78909c)' }}>
                Remembered {day(d.createdAt)} · until {day(d.expiresAt)}{d.lastUsedAt ? ` · last used ${day(d.lastUsedAt)}` : ''}
              </span>
            </span>
            <button onClick={() => forget(d.id)} disabled={!!busy} style={S.linkBtn}>{busy === d.id ? 'Forgetting…' : 'Forget'}</button>
          </li>
        ))}
      </ul>
      {devices.length > 1 && <button onClick={() => forget(null)} disabled={!!busy} style={{ ...S.linkBtn, marginTop: 6 }}>{busy === 'all' ? 'Forgetting…' : 'Forget all devices'}</button>}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <dt style={S.dt}>{label}</dt>
      <dd style={S.dd}>{value || '—'}</dd>
    </div>
  );
}

const S = {
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap', padding: '10px 22px', borderBottom: '1px solid var(--c-eceff1)' },   // as in Security (3.73.0)
  tab: { ...PILL, display: 'inline-flex', alignItems: 'center', gap: 6 },
  tabOn: PILL_ON,
  overlay: {
    position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px 16px'   // top-aligned (3.73.0): the tabs stay put when a tab is shorter
  },
  modal: {
    background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(600px, 100%)', maxHeight: '88vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)',
    animation: 'badgePop 0.2s ease-out'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    padding: '18px 22px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)', borderRadius: '14px 14px 0 0'
  },
  who: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  avatarBtn: { position: 'relative', padding: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: '50%' },
  avatarEdit: { position: 'absolute', right: -4, bottom: -4, width: 20, height: 20, borderRadius: '50%', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-b2dfdb)', color: 'var(--t-00695c)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  avatar: {
    width: 42, height: 42, borderRadius: '50%', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700
  },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)', fontFamily: 'inherit' },
  sub: { fontSize: 13, color: 'var(--t-607d8b)', overflowWrap: 'anywhere' },
  closeBtn: {
    width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)',
    cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit', flexShrink: 0
  },
  body: { overflowY: 'auto', padding: '18px 22px 22px' },
  twoCols: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, alignItems: 'end' },
  phoneRow: { display: 'flex', gap: 8, alignItems: 'flex-end', minWidth: 0 },
  details: { margin: '16px 0 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 20px' },
  dt: { fontSize: 11, fontWeight: 700, color: 'var(--t-607d8b)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  dd: { margin: '3px 0 0', fontSize: 14, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  note: { fontSize: 13, color: 'var(--t-607d8b)', margin: '10px 0 0', lineHeight: 1.5 },
  waRow: { gridColumn: '1 / -1', fontSize: 13, color: 'var(--t-607d8b)', margin: 0, lineHeight: 1.5 },
  optional: { fontWeight: 400, color: 'var(--t-90a4ae)' },
  section: { marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--c-eceff1)' },
  sectionTitle: { margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--t-37474f)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  form: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--t-546e7a)' },
  input: {
    padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14,
    color: 'var(--t-263238)', fontFamily: 'inherit'
  },
  codeInput: { fontSize: 20, letterSpacing: '6px', fontFamily: 'monospace', maxWidth: 180 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '9px 12px', borderRadius: 8, fontSize: 13 },
  saveBtn: {
    alignSelf: 'flex-start', padding: '10px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  btnOff: { opacity: 0.45, cursor: 'default' },
  secondaryBtn: {
    padding: '10px 16px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  dangerBtn: {
    alignSelf: 'flex-start', padding: '10px 18px', background: 'var(--c-ffffff)', color: 'var(--t-b71c1c)', border: '1.5px solid var(--c-ef9a9a)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  linkBtn: {
    padding: '10px 6px', background: 'none', border: 'none', color: 'var(--t-546e7a)', cursor: 'pointer',
    fontSize: 14, fontFamily: 'inherit', textDecoration: 'underline'
  },
  status: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.5, margin: '10px 0 12px' },
  pill: { flexShrink: 0, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  pillOn: { background: 'var(--c-e8f5e9)', color: 'var(--t-1b5e20)' },
  pillOff: { background: 'var(--c-eceff1)', color: 'var(--t-546e7a)' },
  pillWarn: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)' },
  box: { display: 'flex', flexDirection: 'column', gap: 12, padding: 14, border: '1.5px solid var(--c-e0f2f1)', borderRadius: 10, background: 'var(--c-fbfefd)' },
  boxText: { margin: 0, fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.5 },
  row: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' },
  steps: { margin: 0, paddingLeft: 20, fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.7 },
  scanRow: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' },
  qr: { width: 160, height: 160, border: '1px solid var(--c-eceff1)', borderRadius: 8 },
  key: { display: 'block', marginTop: 4, fontSize: 14, fontFamily: 'monospace', color: 'var(--t-263238)', overflowWrap: 'anywhere', letterSpacing: '1px' },
};

// Shared with Profile picture and the Time zone pop-up (3.22.2), so they look the same
export const dialogStyles = S;
